import { inject } from '@angular/core';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { UserAuthService } from '@app/core/user/user.service';
import { AuthService } from '@app/core/auth/auth.service';
import { forkJoin, from, Observable, of, switchMap } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';
import { ClientsService } from './shared/services/preloaded/auth/clients.service';
import { CategoriesService } from './shared/services/preloaded/auth/categories.service';
import { RegionsService } from './shared/services/preloaded/unauth/regions.service';
import { ProvidersService } from './shared/services/preloaded/unauth/providers.service';
import { ExternalApisService } from './shared/services/preloaded/unauth/external-apis.service';
import { ColorsService } from './shared/services/preloaded/unauth/colors.service';
import { PrintPricesService } from './shared/services/preloaded/unauth/print-prices.service';
import { CredentialsCurrentUser, CredentialsService } from '@app/core';
import { BestSellersService } from './shared/services/preloaded/auth/best-sellers.service';
import { HomePageService } from './shared/services/preloaded/auth/home-page.service';
import { UserClientOptionService } from './shared/services/preloaded/auth/user-client-option.service';
import { UserBrandSettingPreloadService } from './shared/services/preloaded/auth/user-brand-setting.service';
import { PremiumSessionCartService } from './shared/services/preloaded/auth/cart.service';
import { BrandsService } from './shared/services/preloaded/auth/brands.service';
import { ProviderSettingService } from './shared/services/preloaded/unauth/provider-setting.service';
import { ROLES_GROUPS } from '@app/constants';

type AuthenticatedSessionDeps = {
    bestSellersService: BestSellersService;
    homePageService: HomePageService;
    userClientOptionService: UserClientOptionService;
    userBrandSettingPreloadService: UserBrandSettingPreloadService;
    premiumSessionCartService: PremiumSessionCartService;
};

type AuthenticatedBaseCatalogDeps = {
    clientsService: ClientsService;
    categoriesService: CategoriesService;
    regionsService: RegionsService;
    providersService: ProvidersService;
    providerSettingService: ProviderSettingService;
    externalApisService: ExternalApisService;
    colorsService: ColorsService;
    printPricesService: PrintPricesService;
    brandsService: BrandsService;
};

type AuthenticatedPreloadDeps = AuthenticatedBaseCatalogDeps & {
    navigationService: NavigationService;
    sessionDeps: AuthenticatedSessionDeps;
};

type PublicPreloadDeps = {
    colorsService: ColorsService;
    navigationService: NavigationService;
    categoriesService: CategoriesService;
    bestSellersService: BestSellersService;
};

type HomeDataDeps = {
    colorsService: ColorsService;
    navigationService: NavigationService;
    categoriesService: CategoriesService;
    bestSellersService: BestSellersService;
    clientsService: ClientsService;
    regionsService: RegionsService;
    providersService: ProvidersService;
    externalApisService: ExternalApisService;
    brandsService: BrandsService;
    providerSettingService: ProviderSettingService;
    printPricesService: PrintPricesService;
    homePageService: HomePageService;
    userClientOptionService: UserClientOptionService;
};

/**
 * Aísla cada precarga: un error (o respuesta vacía) de categorías, productos o
 * cualquier otro catálogo no debe cancelar la navegación ni dejar la app en blanco.
 */
function safePreload<T>(
    source: Observable<T>,
    label: string
): Observable<T | null> {
    return source.pipe(
        catchError(error => {
            console.error(
                `[preload] ${label} falló; se continúa sin esos datos`,
                error
            );
            return of(null);
        })
    );
}

function safeFrom<T>(promise: Promise<T>, label: string): Observable<T | null> {
    return safePreload(from(promise), label);
}

function ensureSessionUser(
    credentialsService: CredentialsService,
    userAuthService: UserAuthService,
    authService: AuthService
): Observable<CredentialsCurrentUser | null> {
    const currentUser = credentialsService.credentialsCurrentUser;
    if (currentUser) {
        return of(currentUser);
    }

    return authService.check().pipe(
        switchMap(authenticated => {
            const resolvedUser = credentialsService.credentialsCurrentUser;
            if (resolvedUser) {
                return of(resolvedUser);
            }

            if (!authenticated) {
                return of(null);
            }

            return userAuthService.user$.pipe(
                filter((user): user is CredentialsCurrentUser => !!user),
                take(1)
            );
        })
    );
}

function loadAuthenticatedSessionData(
    user: CredentialsCurrentUser | null,
    deps: AuthenticatedSessionDeps
): Observable<unknown> {
    const isMktUser = !!user && ROLES_GROUPS.MKT_GROUP.includes(user.role);

    return forkJoin([
        safeFrom(deps.bestSellersService.listBestSellers(user), 'bestSellers'),
        safeFrom(deps.homePageService.listHomePageLists(user), 'homePage'),
        safePreload(
            deps.userClientOptionService.getClientAndClientOptions(),
            'clientOptions'
        ),
        isMktUser
            ? safeFrom(
                  deps.userBrandSettingPreloadService.loadForUser(user),
                  'userBrandSetting'
              )
            : of(null),
        isMktUser
            ? deps.premiumSessionCartService.isLoadedForUser(user)
                ? of(deps.premiumSessionCartService.getCartSnapshot())
                : safeFrom(
                      deps.premiumSessionCartService.loadCart(user),
                      'premiumCart'
                  )
            : of(null)
    ]);
}

function loadAuthenticatedBaseCatalogData(
    deps: AuthenticatedBaseCatalogDeps
): Observable<unknown> {
    return forkJoin([
        safeFrom(deps.clientsService.listClients(), 'clients'),
        safeFrom(deps.categoriesService.ensurePreloaded(), 'categories'),
        safeFrom(deps.regionsService.listRegions(), 'regions'),
        safeFrom(deps.providersService.listProviders(), 'providers'),
        safeFrom(
            deps.providerSettingService.listProviderSettings(),
            'providerSettings'
        ),
        safeFrom(deps.externalApisService.listExternalApis(), 'externalApis'),
        safeFrom(deps.colorsService.listColors(), 'colors'),
        safeFrom(deps.printPricesService.listPrintPrices(), 'printPrices'),
        safeFrom(deps.brandsService.listBrands(), 'brands')
    ]);
}

function runAuthenticatedPreload(
    deps: AuthenticatedPreloadDeps,
    user: CredentialsCurrentUser | null
): Observable<unknown> {
    const clientContext$ =
        user?.clientId && ROLES_GROUPS.MKT_GROUP.includes(user.role)
            ? safePreload(
                  deps.sessionDeps.userClientOptionService.getClientAndClientOptions(),
                  'clientOptions'
              )
            : of(null);

    return clientContext$.pipe(
        switchMap(() => loadAuthenticatedBaseCatalogData(deps)),
        switchMap(() => loadAuthenticatedSessionData(user, deps.sessionDeps)),
        switchMap(() =>
            safePreload(deps.navigationService.get(user), 'navigation')
        )
    );
}

function runPublicPreload(deps: PublicPreloadDeps): Observable<unknown> {
    return forkJoin([
        safeFrom(deps.colorsService.listColors(), 'colors'),
        safePreload(deps.navigationService.get(null), 'navigation'),
        safeFrom(deps.categoriesService.ensurePreloaded(), 'categories'),
        safeFrom(deps.bestSellersService.listBestSellers(null), 'bestSellers')
    ]);
}

function injectHomeDataDeps(): HomeDataDeps {
    return {
        colorsService: inject(ColorsService),
        navigationService: inject(NavigationService),
        categoriesService: inject(CategoriesService),
        bestSellersService: inject(BestSellersService),
        clientsService: inject(ClientsService),
        regionsService: inject(RegionsService),
        providersService: inject(ProvidersService),
        externalApisService: inject(ExternalApisService),
        brandsService: inject(BrandsService),
        providerSettingService: inject(ProviderSettingService),
        printPricesService: inject(PrintPricesService),
        homePageService: inject(HomePageService),
        userClientOptionService: inject(UserClientOptionService)
    };
}

function injectAuthenticatedPreloadDeps(): AuthenticatedPreloadDeps {
    return {
        navigationService: inject(NavigationService),
        clientsService: inject(ClientsService),
        categoriesService: inject(CategoriesService),
        regionsService: inject(RegionsService),
        providersService: inject(ProvidersService),
        providerSettingService: inject(ProviderSettingService),
        externalApisService: inject(ExternalApisService),
        colorsService: inject(ColorsService),
        printPricesService: inject(PrintPricesService),
        brandsService: inject(BrandsService),
        sessionDeps: {
            bestSellersService: inject(BestSellersService),
            homePageService: inject(HomePageService),
            userClientOptionService: inject(UserClientOptionService),
            userBrandSettingPreloadService: inject(
                UserBrandSettingPreloadService
            ),
            premiumSessionCartService: inject(PremiumSessionCartService)
        }
    };
}

function injectPublicPreloadDeps(): PublicPreloadDeps {
    return {
        colorsService: inject(ColorsService),
        navigationService: inject(NavigationService),
        categoriesService: inject(CategoriesService),
        bestSellersService: inject(BestSellersService)
    };
}

function buildHomeDataObservable(
    user: CredentialsCurrentUser | null,
    deps: HomeDataDeps
): Observable<unknown> {
    const publicPreload$ = forkJoin({
        colors: deps.colorsService.colors$.pipe(take(1)),
        categories: deps.categoriesService.categories$.pipe(take(1)),
        categoryTree: deps.categoriesService.categoriesTree$.pipe(take(1)),
        bestSellers: deps.bestSellersService.bestSellers$.pipe(take(1))
    });

    if (!user) {
        return publicPreload$;
    }

    return forkJoin({
        public: publicPreload$,
        clients: deps.clientsService.clients$.pipe(take(1)),
        regions: deps.regionsService.regions$.pipe(take(1)),
        providers: deps.providersService.providers$.pipe(take(1)),
        externalApis: deps.externalApisService.externalApis$.pipe(take(1)),
        brands: deps.brandsService.brands$.pipe(take(1)),
        providerSettings: deps.providerSettingService.providersSetting$.pipe(
            take(1)
        ),
        printPrices: deps.printPricesService.printPrices$.pipe(take(1)),
        homePageWallItems: deps.homePageService.homePageWallItems$.pipe(
            take(1)
        ),
        client: deps.userClientOptionService.client$.pipe(take(1)),
        clientOption: deps.userClientOptionService.clientOption$.pipe(take(1))
    });
}

/** Precarga BD para invitados. */
export const publicPreloadDataResolver = (): Observable<unknown> =>
    runPublicPreload(injectPublicPreloadDeps());

/** Precarga BD para sesión autenticada. */
export const authenticatedPreloadDataResolver = (): Observable<unknown> => {
    const credentialsService = inject(CredentialsService);
    const userAuthService = inject(UserAuthService);
    const authService = inject(AuthService);
    const authPreloadDeps = injectAuthenticatedPreloadDeps();

    return ensureSessionUser(
        credentialsService,
        userAuthService,
        authService
    ).pipe(switchMap(user => runAuthenticatedPreload(authPreloadDeps, user)));
};

/** Espera datos precargados (sin BD). */
export const homeDataResolver = (): Observable<unknown> => {
    const credentialsService = inject(CredentialsService);
    const userAuthService = inject(UserAuthService);
    const authService = inject(AuthService);
    const homeDeps = injectHomeDataDeps();

    return ensureSessionUser(
        credentialsService,
        userAuthService,
        authService
    ).pipe(switchMap(user => buildHomeDataObservable(user, homeDeps)));
};

/** Layout /home y /premiums. */
export const publicLayoutInitialDataResolver = (): Observable<unknown> => {
    const credentialsService = inject(CredentialsService);
    const userAuthService = inject(UserAuthService);
    const authService = inject(AuthService);
    const homeDeps = injectHomeDataDeps();
    const authPreloadDeps = injectAuthenticatedPreloadDeps();
    const publicPreloadDeps = injectPublicPreloadDeps();

    return ensureSessionUser(
        credentialsService,
        userAuthService,
        authService
    ).pipe(
        switchMap(user => {
            const preload$ = user
                ? runAuthenticatedPreload(authPreloadDeps, user)
                : runPublicPreload(publicPreloadDeps);

            return preload$.pipe(
                switchMap(() => buildHomeDataObservable(user, homeDeps))
            );
        })
    );
};

/** Layout autenticado (AuthGuard). */
export const authenticatedLayoutInitialDataResolver = (): Observable<unknown> => {
    const credentialsService = inject(CredentialsService);
    const userAuthService = inject(UserAuthService);
    const authService = inject(AuthService);
    const homeDeps = injectHomeDataDeps();
    const authPreloadDeps = injectAuthenticatedPreloadDeps();

    return ensureSessionUser(
        credentialsService,
        userAuthService,
        authService
    ).pipe(
        switchMap(user =>
            runAuthenticatedPreload(authPreloadDeps, user).pipe(
                switchMap(() => buildHomeDataObservable(user, homeDeps))
            )
        )
    );
};

/** @deprecated Usar authenticatedLayoutInitialDataResolver */
export const initialDataResolver = authenticatedLayoutInitialDataResolver;
